import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BusinessApiService from "../../services/BusinessApiService";

const API_BASE = process.env.REACT_APP_API_URL || "";

export default function ProductGS1Page() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();

  const [product, setProduct]     = useState<any>(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [message, setMessage]     = useState<{text: string; type: 'success'|'error'} | null>(null);

  // GS1 barcode lookup
  const [gtinInput, setGtinInput]     = useState("");
  const [gtinLooking, setGtinLooking] = useState(false);
  const [gtinResult, setGtinResult]   = useState<any>(null);
  const [gtinError, setGtinError]     = useState<string | null>(null);

  // Manufacturer
  const [mfgSearch, setMfgSearch]       = useState("");
  const [mfgSearching, setMfgSearching] = useState(false);
  const [mfgResults, setMfgResults]     = useState<any[]>([]);

  // Editable fields
  const [gtin, setGtin]           = useState("");
  const [mfgName, setMfgName]     = useState("");
  const [mfgCountry, setMfgCountry] = useState("");
  const [mfgAddress, setMfgAddress] = useState("");
  const [mfgWebsite, setMfgWebsite] = useState("");
  const [mfgBusinessId, setMfgBusinessId] = useState("");
  const [mfgVerified, setMfgVerified]     = useState(false);

  const token = BusinessApiService.getBusinessToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ── Load product ──────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    fetch(`${API_BASE}/products/${productId}`, { headers })
      .then(r => r.json())
      .then(data => {
        const p = data.product || data.data || data;
        setProduct(p);
        // Pre-fill from existing data
        setGtin(p.gs1?.gtin || "");
        setMfgName(p.manufacturer?.name || "");
        setMfgCountry(p.manufacturer?.country || "");
        setMfgAddress(p.manufacturer?.address || "");
        setMfgWebsite(p.manufacturer?.website || "");
        setMfgBusinessId(p.manufacturer?.businessId?.toString() || "");
        setMfgVerified(p.manufacturer?.isVerified || false);
      })
      .catch(() => setMessage({ text: "Failed to load product", type: 'error' }))
      .finally(() => setLoading(false));
  }, [productId]);

  // ── GS1 Barcode Lookup ────────────────────────────────────────────────────
  const lookupBarcode = async () => {
    const clean = gtinInput.replace(/\D/g, '');
    if (clean.length < 8) { setGtinError('Enter at least 8 digits'); return; }
    setGtinLooking(true); setGtinError(null); setGtinResult(null);
    try {
      const res  = await fetch(`${API_BASE}/epcis/lookup?gtin=${clean}`, { headers });
      const data = await res.json();
      if (data.success && data.autoFill) {
        setGtinResult(data.autoFill);
        setGtin(clean);
      } else {
        setGtinError('Product not found in GS1 registry');
        setGtin(clean); // still save the GTIN
      }
    } catch { setGtinError('Lookup failed — check connection'); }
    setGtinLooking(false);
  };

  // ── Manufacturer Search ───────────────────────────────────────────────────
  const searchManufacturers = async () => {
    if (mfgSearch.trim().length < 2) return;
    setMfgSearching(true);
    try {
      const res  = await fetch(`${API_BASE}/business/all?search=${encodeURIComponent(mfgSearch)}`, { headers });
      const data = await res.json();
      setMfgResults(data.businesses || data.data || []);
    } catch { setMfgResults([]); }
    setMfgSearching(false);
  };

  const linkManufacturer = (b: any) => {
    setMfgBusinessId(String(b._id || b.businessId || ""));
    setMfgName(b.companyName || b.tradingName || "");
    setMfgCountry(b.address?.country || "");
    setMfgAddress([b.address?.street, b.address?.city, b.address?.state].filter(Boolean).join(', '));
    setMfgWebsite(b.website || "");
    setMfgVerified(true);
    setMfgResults([]);
    setMfgSearch("");
  };

  const clearManufacturer = () => {
    setMfgBusinessId(""); setMfgName(""); setMfgCountry("");
    setMfgAddress(""); setMfgWebsite(""); setMfgVerified(false);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true); setMessage(null);
    try {
      const pid = product?.productId || product?._id || productId;
      const res = await fetch(`${API_BASE}/products/${pid}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          gtin: gtin || undefined,
          manufacturer: (mfgName || mfgBusinessId) ? {
            businessId: mfgBusinessId || undefined,
            name:       mfgName    || undefined,
            country:    mfgCountry || undefined,
            address:    mfgAddress || undefined,
            website:    mfgWebsite || undefined,
          } : null,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage({ text: "✅ GS1 & Manufacturer info saved!", type: 'success' });
        setMfgVerified(!!mfgBusinessId);
      } else {
        setMessage({ text: data.error || data.message || "Save failed", type: 'error' });
      }
    } catch (e: any) {
      setMessage({ text: e.message, type: 'error' });
    }
    setSaving(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center min-h-64">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        <p className="mt-2 text-gray-600">Loading product...</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-6">

      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1">
          ← Back
        </button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">GS1 & Manufacturer</h2>
          <p className="text-gray-500 text-sm">{product?.name} · {product?.sku}</p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200'
          : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Current GS1 Info */}
      {product?.gs1?.gtin && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm">
          <p className="font-semibold text-blue-900 mb-1">Current GS1 Info</p>
          <p className="font-mono text-blue-700">GTIN: {product.gs1.gtin}</p>
          <p className="text-blue-600">
            Status: {product.gs1.status || 'platform'} · 
            {product.gs1.isOwnGtin ? ' ✅ Own GTIN' : ' 🔵 JepTags internal'}
          </p>
        </div>
      )}

      {/* ── GS1 Barcode Lookup ─────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">🏷️ GS1 Barcode</h3>
        <p className="text-sm text-gray-500 mb-4">
          Scan or enter existing product barcode to verify and link GS1 data
        </p>

        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={gtinInput}
            onChange={e => setGtinInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), lookupBarcode())}
            placeholder="Enter GTIN / UPC / EAN (8-14 digits)..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            maxLength={14}
          />
          <button onClick={lookupBarcode} disabled={gtinLooking}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {gtinLooking ? '🔍 Looking...' : '🔍 Lookup'}
          </button>
        </div>

        {gtinError && <p className="text-xs text-red-600 mb-3">⚠️ {gtinError}</p>}

        {gtinResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 text-sm">
            <p className="font-semibold text-green-800 mb-1">✅ Product found in GS1 registry</p>
            {gtinResult.name && <p className="text-gray-700"><strong>Name:</strong> {gtinResult.name}</p>}
            {gtinResult.brand && <p className="text-gray-700"><strong>Brand:</strong> {gtinResult.brand}</p>}
            {gtinResult.category && <p className="text-gray-700"><strong>Category:</strong> {gtinResult.category}</p>}
            <p className="text-xs text-gray-500 mt-1">Source: {gtinResult.source || 'gs1-resolver'}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            GTIN to save <span className="text-gray-400 font-normal">(auto-filled from lookup)</span>
          </label>
          <input
            type="text"
            value={gtin}
            onChange={e => setGtin(e.target.value.replace(/\D/g, ''))}
            placeholder="e.g. 09506000140445"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
            maxLength={14}
          />
        </div>
      </div>

      {/* ── Manufacturer ───────────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">🏭 Manufacturer (MFG)</h3>
        <p className="text-sm text-gray-500 mb-4">
          Link to a verified JepTags manufacturer or enter details manually
        </p>

        {/* Verified badge */}
        {mfgVerified && mfgBusinessId && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4 text-sm text-green-700">
            ✅ Linked to verified JepTags manufacturer: <strong>{mfgName}</strong>
            <button onClick={clearManufacturer} className="ml-auto text-red-500 hover:text-red-700 text-xs">
              ✕ Unlink
            </button>
          </div>
        )}

        {/* Search JepTags */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search JepTags businesses
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={mfgSearch}
              onChange={e => setMfgSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchManufacturers())}
              placeholder="Search by company name..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            />
            <button onClick={searchManufacturers} disabled={mfgSearching}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
              {mfgSearching ? 'Searching...' : '🔍 Search'}
            </button>
          </div>

          {mfgResults.length > 0 && (
            <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
              {mfgResults.slice(0, 5).map((b: any) => (
                <button key={String(b._id)} onClick={() => linkManufacturer(b)}
                  className="w-full text-left px-4 py-3 hover:bg-green-50 border-b last:border-0 text-sm flex items-center justify-between">
                  <div>
                    <span className="font-medium text-gray-900">{b.companyName || b.tradingName}</span>
                    <span className="text-gray-500 text-xs ml-2">{b.address?.country}</span>
                  </div>
                  <span className="text-green-600 text-xs">✅ Verified · Click to link</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Manual entry */}
        <div className="border-t pt-4">
          <p className="text-sm font-medium text-gray-700 mb-3">
            Or enter manually <span className="text-gray-400 font-normal">(for manufacturers not on JepTags)</span>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Manufacturer Name</label>
              <input type="text" value={mfgName} onChange={e => setMfgName(e.target.value)}
                placeholder="e.g. ABC Factory Co."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Country of Origin</label>
              <input type="text" value={mfgCountry} onChange={e => setMfgCountry(e.target.value)}
                placeholder="e.g. Vietnam, China, USA"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Factory Address</label>
              <input type="text" value={mfgAddress} onChange={e => setMfgAddress(e.target.value)}
                placeholder="Street, City, Country"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
              <input type="text" value={mfgWebsite} onChange={e => setMfgWebsite(e.target.value)}
                placeholder="https://factory.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        </div>

        {/* What consumer sees */}
        {(mfgName || mfgCountry) && (
          <div className="mt-4 bg-gray-50 border rounded-lg p-3 text-sm">
            <p className="font-medium text-gray-700 mb-1">📱 Consumer will see:</p>
            <p className="text-gray-600">🏭 Manufactured by: <strong>{mfgName || '—'}</strong></p>
            {mfgCountry && <p className="text-gray-600">📍 Country: <strong>{mfgCountry}</strong></p>}
            {mfgVerified
              ? <p className="text-green-600 text-xs mt-1">✅ Verified JepTags Member</p>
              : <p className="text-yellow-600 text-xs mt-1">⚠️ Unverified (manually entered)</p>
            }
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex gap-3">
        <button onClick={() => navigate(-1)}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
          {saving ? 'Saving...' : '💾 Save GS1 & Manufacturer Info'}
        </button>
      </div>

    </div>
  );
}